// src/auth.ts
import {
    COGNITO_CLIENT_ID,
    COGNITO_DOMAIN,
    COGNITO_REDIRECT_URI,
    COGNITO_SCOPES,
    COGNITO_LOGOUT_URI,
} from "./authConfig";

const STORAGE_KEY_CODE_VERIFIER = "auth_code_verifier";
const STORAGE_KEY_STATE = "auth_state";
const STORAGE_KEY_TOKENS = "auth_tokens";


export interface AuthTokens {
    access_token: string;
    id_token: string;
    refresh_token?: string;
    expires_in: number;
    token_type: string;
    received_at: number; // ms epoch
}

function arrayBufferToBase64Url(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = "";
    for (let i = 0; i < bytes.byteLength; i++) {
        binary += String.fromCharCode(bytes[i]);
    }
    const base64 = btoa(binary);
    return base64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function randomString(bytesLength = 32): string {
    const bytes = new Uint8Array(bytesLength);
    crypto.getRandomValues(bytes);
    return arrayBufferToBase64Url(bytes.buffer);
}

async function createPkcePair(): Promise<{ verifier: string; challenge: string }> {
    const verifier = randomString(64); // long-ish
    const encoder = new TextEncoder();
    const digest = await crypto.subtle.digest("SHA-256", encoder.encode(verifier));
    const challenge = arrayBufferToBase64Url(digest);
    return { verifier, challenge };
}

export function getStoredTokens(): AuthTokens | null {
    const raw = sessionStorage.getItem(STORAGE_KEY_TOKENS);
    if (!raw) return null;
    try {
        const tokens: AuthTokens = JSON.parse(raw);
        return tokens;
    } catch {
        return null;
    }
}

export function getIdToken(): string | null {
    const tokens = getStoredTokens();
    return tokens?.id_token ?? null;
}

export function getAccessTokenNoRefresh(): string | null {
    const tokens = getStoredTokens();
    return tokens?.access_token ?? null;
}

export function gtRefreshToken(): string | null {
    const tokens = getStoredTokens();
    return tokens?.refresh_token ?? null;
}

export function isLoggedIn(): boolean {
    const tokens = getStoredTokens();
    if (!tokens) return false;
    const now = Date.now() / 1000;
    const expiry = tokens.received_at / 1000 + tokens.expires_in - 60; // 60s fudge
    return now < expiry;
}

/**
 * Kick off the login flow: generate PKCE + state, store them, redirect to Cognito.
 */
export async function redirectToLogin(identityProvider?: string) {
    const { verifier, challenge } = await createPkcePair();
    const state = randomString(32);

    sessionStorage.setItem(STORAGE_KEY_CODE_VERIFIER, verifier);
    sessionStorage.setItem(STORAGE_KEY_STATE, state);

    const params = new URLSearchParams({
        client_id: COGNITO_CLIENT_ID,
        response_type: "code",
        scope: COGNITO_SCOPES,
        redirect_uri: COGNITO_REDIRECT_URI,
        state,
        code_challenge_method: "S256",
        code_challenge: challenge,
    });

    // This is the magic: tell Cognito which IdP to use.
    if (identityProvider) {
        params.set("identity_provider", identityProvider);
    }

    const authUrl = `${COGNITO_DOMAIN}/oauth2/authorize?${params.toString()}`;
    console.log(`Going to ${authUrl}`);
    window.location.href = authUrl;
}

/** RIT SAML login */
export async function redirectToRitLogin() {
    // Use the exact IdP name from Cognito for your RIT provider
    return redirectToLogin("RIT");
}

/** Google login */
export async function redirectToGoogleLogin() {
    // Use the exact IdP name from Cognito for your Google IdP
    return redirectToLogin("Google");
}

/**
 * Global promise cache for OAuth redirect callback handling.
 *
 * PURPOSE:
 * This exists solely to handle React Strict Mode's double-invocation of effects.
 * In development, React intentionally runs effects twice to surface bugs. Without
 * this cache, both invocations would try to exchange the OAuth code simultaneously,
 * causing race conditions and authentication state corruption.
 *
 * WHY IT'S SAFE:
 * - The promise is only cached while a code exchange is in-flight
 * - It's cleared immediately after completion (deterministic, not timeout-based)
 * - The URL parameter is removed FIRST, so subsequent calls short-circuit
 *
 * LIFECYCLE:
 * 1. First effect invocation: creates promise, removes URL param, exchanges code
 * 2. Second effect invocation: sees no URL param, returns false immediately
 * 3. First invocation completes: clears cache
 *
 * This is a module-level variable (not ideal for testability), but it's the
 * pragmatic solution for ensuring idempotent OAuth callback handling.
 */
let redirectCallbackPromise: Promise<boolean> | null = null;

/**
 * Called on page load: if there's a ?code=... in the URL, exchange it for tokens.
 * Returns true if handled (and tokens stored), false if no code present.
 */
export async function handleRedirectCallback(): Promise<boolean> {

    /*
     * Defense-in-depth: If somehow both invocations make it past the URL check
     * (shouldn't happen due to the replaceState above, but belt-and-suspenders),
     * reuse the existing promise rather than starting a second token exchange.
     *
     * This is the "suspenders" in our "belt and suspenders" approach. The "belt"
     * is removing the URL parameter above. This cache shouldn't actually be needed
     * in practice, but it protects us if there's any timing weirdness we haven't
     * anticipated.
     */
    if (redirectCallbackPromise) {
        console.log("[auth] a redirect callback is already in progress");
        return redirectCallbackPromise;
    }

    const url = new URL(window.location.href);
    const code = url.searchParams.get("code");
    const state = url.searchParams.get("state");

    if (!code) return false;


    /*
     * CRITICAL: Remove the code from the URL immediately, BEFORE we do anything else.
     *
     * This is the key to making this function idempotent. In React Strict Mode, the
     * AuthProvider's useEffect runs twice. By removing the URL parameter first:
     *
     * - The first invocation sees the code, removes it, and starts the exchange
     * - The second invocation sees NO code (it's gone!), returns false, does nothing
     *
     * Without this, both invocations would try to exchange the same code, causing:
     * - Duplicate API calls to the token endpoint
     * - Potential "code already used" errors from the OAuth provider
     * - Race conditions in setting authentication state
     *
     * This approach is deterministic (no timeouts or sleeps) and works because:
     * 1. URL manipulation is synchronous
     * 2. Both effect invocations read from the same URL
     * 3. The first one to execute removes the code for everyone
     */
    window.history.replaceState({}, '', window.location.pathname);

    /*
     * We are going to create an explicit Promise to handle the callback. This allows us
     * to hold the existence of this callback in a global (kinda yuck) so that if we are
     * in react developer/strict mode it won't try to run two authentication cycles at once.
     *
     * Why not use a boolean flag? Because we need callers to wait for the SAME result.
     * A boolean would require complex coordination. A promise naturally handles this:
     * multiple await statements on the same promise all resolve with the same value.
     */
    redirectCallbackPromise = (async () => {

        try {
            const expectedState = sessionStorage.getItem(STORAGE_KEY_STATE);
            const codeVerifier = sessionStorage.getItem(STORAGE_KEY_CODE_VERIFIER);

            if (!expectedState || !codeVerifier || state !== expectedState) {
                console.error("State mismatch or missing PKCE verifier");
                throw new Error("Invalid auth state");
            }

            // Now that we're validated, we can remove the stored PKCE/state
            sessionStorage.removeItem(STORAGE_KEY_STATE);
            sessionStorage.removeItem(STORAGE_KEY_CODE_VERIFIER);

            const body = new URLSearchParams({
                grant_type: "authorization_code",
                client_id: COGNITO_CLIENT_ID,
                code,
                redirect_uri: COGNITO_REDIRECT_URI,
                code_verifier: codeVerifier,
            });

            const tokenUrl = `${COGNITO_DOMAIN}/oauth2/token`;

            const resp = await fetch(tokenUrl, {
                method: "POST",
                headers: {"Content-Type": "application/x-www-form-urlencoded"},
                body: body.toString(),
            });

            if (!resp.ok) {
                const text = await resp.text();
                console.error("Token exchange failed", resp.status, text);
                throw new Error("Token exchange failed");
            }

            const data = await resp.json();

            const tokens: AuthTokens = {
                access_token: data.access_token,
                id_token: data.id_token,
                refresh_token: data.refresh_token,
                expires_in: data.expires_in,
                token_type: data.token_type,
                received_at: Date.now(),
            };

            sessionStorage.setItem(STORAGE_KEY_TOKENS, JSON.stringify(tokens));

            // ✅ Only AFTER success, clean up URL (prevents the race)
            url.searchParams.delete("code");
            url.searchParams.delete("state");
            window.history.replaceState({}, document.title, url.toString());

            return true;
        } catch (error) {
            console.error("Token exchange failed', error")
            return false;
        }

    })();

    const result = await redirectCallbackPromise;
    redirectCallbackPromise = null;
    return result;
}




export function logout() {
    sessionStorage.removeItem(STORAGE_KEY_TOKENS);

    const params = new URLSearchParams({
        client_id: COGNITO_CLIENT_ID,
        logout_uri: COGNITO_LOGOUT_URI,
        redirect_uri: COGNITO_LOGOUT_URI
    });

    const url = `${COGNITO_DOMAIN}/logout?${params.toString()}`;
    window.location.href = url;
}



export async function refreshTokens(): Promise<AuthTokens | null> {
    const tokens = getStoredTokens();
    if (!tokens || !tokens.refresh_token) {
        console.warn("No refresh token available");
        return null;
    }

    const body = new URLSearchParams({
        grant_type: "refresh_token",
        client_id: COGNITO_CLIENT_ID,
        refresh_token: tokens.refresh_token,
    });

    const tokenUrl = `${COGNITO_DOMAIN}/oauth2/token`;

    const resp = await fetch(tokenUrl, {
        method: "POST",
        headers: {
            "Content-Type": "application/x-www-form-urlencoded",
        },
        body: body.toString(),
    });

    if (!resp.ok) {
        const text = await resp.text();
        console.error("Refresh token request failed", resp.status, text);
        return null;
    }

    const data = await resp.json();

    const newTokens: AuthTokens = {
        access_token: data.access_token,
        id_token: data.id_token,
        // Cognito may or may not return a new refresh token – keep the old one if missing
        refresh_token: data.refresh_token ?? tokens.refresh_token,
        expires_in: data.expires_in,
        token_type: data.token_type,
        received_at: Date.now(),
    };

    sessionStorage.setItem(STORAGE_KEY_TOKENS, JSON.stringify(newTokens));
    return newTokens;
}

export async function getAccessToken(): Promise<string | null> {
    const tokens = getStoredTokens();
    if (!tokens) return null;

    const now = Date.now() / 1000;
    const expiry = tokens.received_at / 1000 + tokens.expires_in - 60; // 60s buffer

    if (now < expiry) {
        return tokens.access_token;
    }

    // expired – try to refresh
    const refreshed = await refreshTokens();
    return refreshed?.access_token ?? null;
}
